/**
 * One tab's reads, as the four things every tab on this screen draws them with.
 *
 * The three tabs that read were writing the same block out three times: the enablement predicate
 * beside `isPending`, the three-state reduction, the skeleton gate and a retry that refetches
 * everything. It is written here once, and a caller no longer has to know either rule.
 *
 * ── The enablement predicate is not optional ────────────────────────────────────────────────
 * A DISABLED query reports `isPending` for ever — TanStack derives the flag from "no data and no
 * error", which a query that will never run satisfies for the life of the screen. A gate reading
 * the flag alone draws a skeleton that never retires, which is why `active` is a parameter here
 * rather than something each tab remembers to fold in.
 *
 * ── The GATE owns the whole wait, `aria-busy` included ──────────────────────────────────────
 * `waiting` is what a tab forks on: it stays true until the bars have had their 300ms minimum
 * life, which is after the read answered. Everything that describes the wait hangs off it —
 * the branch, `aria-busy` and the one hidden „waiting" sentence beside it (owner, 21 September
 * 2026). A card still standing bars has not finished changing, so reporting it settled the moment
 * the read landed is a claim a listener acts on 300ms early. A tab that forks its bars on
 * `state === 'pending'` flashes its content in at the read and blinks it out and back at the
 * hand-over, and one that forks only `aria-busy` there says so out loud.
 *
 * `state` is kept for the other two states: a tab still has to tell an ERROR from a READY answer,
 * and the gate knows nothing about either. A WRITE in flight is a different thing again and keeps
 * its own `aria-busy`.
 *
 * Pure wiring: it decides nothing about what a tab says, only when it may say it.
 */

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

/** Three states, never a branch on the data — `CLAUDE.md`'s rule for every read in this app. */
export type SectionState = 'pending' | 'error' | 'ready';

/** As much of a TanStack query as this gate looks at. */
export interface SectionQuery {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => unknown;
}

export interface SectionReadOptions {
  /** Whether the tab that draws these reads is the one on screen. */
  readonly active: boolean;
  /** Every read the section needs. It waits for their union, never for whichever answers first. */
  readonly reads: readonly SectionQuery[];
  /** A failure no query reports — a session carrying no trainer row. */
  readonly failed?: boolean;
  /** Something the section still lacks once the reads are in — a projection that could not build. */
  readonly incomplete?: boolean;
}

export interface SectionRead {
  /**
   * What the READ says, and the only thing that can tell an error from a ready answer. It is NOT
   * what `aria-busy` or the hidden sentence key on — those follow `waiting` with the bars. See
   * the header.
   */
  readonly state: SectionState;
  /** Whether the wait belongs on screen — the read, plus the bars' minimum life. Fork on this. */
  readonly waiting: boolean;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly onRetry: () => void;
}

export function useSectionRead({
  active,
  reads,
  failed = false,
  incomplete = false,
}: SectionReadOptions): SectionRead {
  const pending = active && reads.some((read) => read.isPending);
  const gate = useSkeletonGate(pending);

  const state: SectionState =
    failed || reads.some((read) => read.isError)
      ? 'error'
      : pending || incomplete
        ? 'pending'
        : 'ready';

  return {
    state,
    // The GATE alone. `incomplete` is NOT folded in: it stays true for ever on a failed read — the
    // projection never builds — and a wait that never ends would swallow the failure the tab draws.
    waiting: gate.waiting,
    skeleton: gate.skeleton,
    reveal: gate.reveal,
    onRetry: () => {
      for (const read of reads) void read.refetch();
    },
  };
}
